import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, User as UserIcon, Check, ArrowRight } from 'lucide-react';
import { memberPortal, useAsync } from '@fitboxx/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatDay, formatTime } from '@/lib/utils';

interface Occurrence {
  id: string;
  starts_at: string; ends_at?: string;
  template_name?: string; template?: { name?: string };
  location_name?: string; location?: { name?: string };
  instructor_name?: string; instructor?: { display_name?: string };
  capacity?: number; booked_count?: number;
  booked_by_me?: boolean;
}

function name(o: Occurrence): string {
  return o.template_name || o.template?.name || 'Class';
}
function loc(o: Occurrence): string | undefined {
  return o.location_name || o.location?.name;
}
function instructor(o: Occurrence): string | undefined {
  return o.instructor_name || o.instructor?.display_name;
}

export default function Schedule() {
  const { data, loading, refresh } = useAsync(
    () => memberPortal.upcomingClasses(14) as Promise<Occurrence[]>,
  );
  const rows = Array.isArray(data) ? data : [];
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Group by date
  const byDay = new Map<string, Occurrence[]>();
  for (const o of rows) {
    const d = o.starts_at.slice(0, 10);
    if (!byDay.has(d)) byDay.set(d, []);
    byDay.get(d)!.push(o);
  }

  async function book(o: Occurrence) {
    setBookingId(o.id); setErr(null);
    try {
      await memberPortal.book(o.id);
      refresh();
    } catch (e) { setErr((e as Error).message || 'Could not book this class.'); }
    finally { setBookingId(null); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Schedule</h1>
        <Button asChild variant="outline" size="sm">
          <Link to="/bookings">My bookings <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
        </Button>
      </div>

      {err && <div className="text-sm rounded-md bg-destructive/10 text-destructive px-3 py-2">{err}</div>}

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          <Calendar className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
          No upcoming classes.
        </Card>
      ) : (
        Array.from(byDay.entries()).map(([day, items]) => (
          <div key={day} className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1 pt-2">
              {formatDay(day)}
            </div>
            {items.map((o) => {
              const cap = o.capacity ?? 0;
              const bk = o.booked_count ?? 0;
              const full = cap > 0 && bk >= cap;
              return (
                <Card key={o.id} className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="text-center min-w-[56px]">
                      <div className="text-xs text-muted-foreground">{formatTime(o.starts_at).split(' ')[1] || ''}</div>
                      <div className="font-semibold tabular-nums text-sm">
                        {formatTime(o.starts_at)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{name(o)}</div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        {instructor(o) && <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" />{instructor(o)}</span>}
                        {loc(o) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{loc(o)}</span>}
                        {cap > 0 && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{bk}/{cap}</span>}
                      </div>
                    </div>
                    <div>
                      {o.booked_by_me ? (
                        <Button size="sm" variant="outline" disabled className="text-emerald-700 border-emerald-200">
                          <Check className="h-3.5 w-3.5 mr-1" />Booked
                        </Button>
                      ) : full ? (
                        <Button size="sm" variant="outline" disabled>Full</Button>
                      ) : (
                        <Button size="sm" disabled={bookingId === o.id} onClick={() => void book(o)}>
                          {bookingId === o.id ? '…' : 'Book'}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
